/*
 * ONDO Configurator — store / state machine (WP-A, C-A §7). Pure ES module: no DOM, Node-testable.
 *
 *   import { createStore } from '<asset url>';
 *   const store = createStore(data);     // data = the JSON of snippets/ondo-configurator-data.liquid (C-A §6.1)
 *   store.init();
 *   store.addEventListener('change', (e) => { const { cause, state, prev } = e.detail; … });   // or store.on(fn) → off
 *
 * State (immutable: every change replaces the state object and the arrays/objects it touches):
 *   { step: 'base'|'charms', groupId, base: { pid, vid }  (default: defaultBase() = first available product of the group),
 *     charms:  [{ uid, vid, seq }]   ON the base, left → right
 *     outside: [{ uid, vid, seq }]   outside the base (base max exceeded), display order
 *     seq, selected: uid|null,
 *     browse:  { group: 'all'|charmGroupId, product: pid|null },
 *     filters: { colors: [key], price: null|[lo, hi] (cents), avail: ['1'|'0'] },
 *     view:    { zoom, panX, panY }  (pan in CSS px; setZoom rescales pan by z'/z = centre-anchored zoom),
 *     ui:      { popup: null|'start'|'changeBase'|'filters', summaryOpen, busy, banner: null|'overflow', edit: null|{ cfg } } }
 *
 * Change causes (detail.cause.type): init, step, group, base:product, base:variant (+moved, productChanged),
 *   charm:add (+uid, vid), charm:remove (+uids), charm:move (+uid, swapped, from, to), select, browse, filters, view, ui (+keys),
 *   load (+missing), reset, stock (reserved quantities changed: re-check canAdd states).
 *
 * Ids: every *id parameter accepts a number or a numeric string; state and results always hold numbers.
 *
 * Geometry (wave 4 B2 + R2, wave 8): layout() works in model units mu; refW = the drawn width (mu) of EVERY base at 100 % = the
 * fill (refW = widest base image px, a unit only). Base images are NOT trusted for their pixel width (the same cord is uploaded at
 * different resolutions) and the length option does not change the drawing (wave 8: all base images are drawn the same size): every
 * base variant is drawn refW wide, the height follows each image's own aspect ratio. Charm images are
 * scaled by `charmK` (mu per charm-image px: the stage passes its own, the capture uses charmK('d')). Every box exists twice:
 * the full image box (drawing) and the VISIBLE box measured once per image by `store.trims` (alpha / ink bounding box,
 * resolution-independent fractions) used for pitch, hit areas, selection, extents, tile framing and capture.
 * The DOM is touched only inside trims.load() (canvas scan), so the module stays Node-importable.
 */

export const WIDTHS = [160, 240, 320, 480, 640, 800, 1000, 1200, 1600, 2000, 2400, 3000, 3600, 4500];

/* ———————————————————————— helpers (exported) ———————————————————————— */

function withDelimiters(cents, precision, thousands, decimal) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '0';
  const neg = n < 0;
  const fixed = (Math.abs(n) / 100).toFixed(precision);
  const [int, dec] = fixed.split('.');
  const intD = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  return (neg ? '-' : '') + intD + (dec ? decimal + dec : '');
}

/** Shopify money format: formatMoney(123456, '€{{amount_with_comma_separator}}') === '€1.234,56'. HTML tags are stripped. */
export function formatMoney(cents, format) {
  const fmt = String(format || '€{{amount_with_comma_separator}}').replace(/<[^>]*>/g, '');
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
    switch (key) {
      case 'amount': return withDelimiters(cents, 2, ',', '.');
      case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ', '');
      case 'amount_with_space_separator': return withDelimiters(cents, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return withDelimiters(cents, 2, ' ', '.');
      default: return withDelimiters(cents, 2, ',', '.');
    }
  });
}

/** Shopify CDN url at a given width: appends/replaces `width` (Liquid image_url is printed without width). */
export function imgUrl(src, width) {
  if (!src) return '';
  const w = Math.max(1, Math.round(Number(width) || 0));
  if (!w) return src;
  if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, `$1width=${w}`);
  return src + (src.includes('?') ? '&' : '?') + 'width=' + w;
}

/** srcset with WIDTHS capped at the natural width (and at maxW when given). */
export function srcset(src, naturalW, maxW) {
  if (!src) return '';
  const cap = Math.min(Number(naturalW) || Infinity, Number(maxW) || Infinity);
  let list = WIDTHS.filter((w) => w <= cap);
  if (Number.isFinite(cap) && (!list.length || list[list.length - 1] < cap) && cap <= WIDTHS[WIDTHS.length - 1] * 1.3) {
    list.push(Math.round(cap));
  }
  if (!list.length) list = [WIDTHS[0]];
  return list.map((w) => `${imgUrl(src, w)} ${w}w`).join(', ');
}

/* ———————————————————————— image trims (B2) ————————————————————————
 * Visible bounding box of a configurator image, as fractions of its size: { x0, y0, x1, y1, sx0, sx1, mode }.
 * sx0/sx1 = opaque columns within ±6 % of the height around the cord axis (H/2) = the space the charm takes ON the cord.
 * mode: 'a' (transparent image, visible = alpha > 16) | 'ink' (opaque image, visible = min(r,g,b) < 231) | 'est' (estimate).
 * Measured once per URL (canvas ≤ TRIM_SCAN_MAX px on its longest side; Shopify CDN sends ACAO *), cached in memory +
 * localStorage (per-viewer convenience).
 * Persistence: only the keys of the images the page uses (`keep`: stale `?v=` URLs are dropped when seeding), oldest → newest,
 * capped to the newest TRIM_MAX, written at most once per `saveDelay` ms (and on pagehide). */
export const TRIM_EST = Object.freeze({ x0: 0, y0: 0.325, x1: 1, y1: 0.675, sx0: 0, sx1: 1, mode: 'est' });
export const TRIM_KEY = 'ondo-cfg-trims-v1';
export const TRIM_MAX = 800;
/** Base images: ONE natural-width URL (≤ this cap) shared by the trims scan, the stage <img> and the server-rendered first
 *  paint (snippets/ondo-configurator-stage.liquid) → one download per base file (charms keep the 1200 cap).
 *  WIDTH cap of the download URL (`&width=`), not of the height; the scan itself is capped separately (TRIM_SCAN_MAX).
 *  The value is written three times — change all three together: here, `B.side || 4000` in assets/ondo-configurator-stage.js
 *  (fallback) and `at_most: 4000` in snippets/ondo-configurator-stage.liquid (server render), or the base downloads twice. */
export const BASE_MAX_SIDE = 4000;
/** Longest side of the canvas the trims are measured on (V-R3-CODE MINOR 1): trims are fractions, so a downscaled bitmap
 *  gives the same box (± 1 / TRIM_SCAN_MAX) at a bounded cost — a 4000 px base would otherwise be scanned at full size
 *  (≈ 110 MB, ~350 ms main thread on a mid-range phone; iOS refuses canvases over 16.7 MP). The download is unchanged. */
export const TRIM_SCAN_MAX = 1200;
const TIMED_OUT = Object.freeze({ timeout: true });   // measure() sentinel: transient, retried once (not a permanent failure)

const fracOk = (a, b) => Number.isFinite(a) && Number.isFinite(b) && a >= 0 && a < b && b <= 1;

export function createTrims({
  persist = true, concurrency = 4, timeout = 8000, maxSide = 1200, keep = null, saveDelay = 1000, retryDelay = 5000,
  scanMax = TRIM_SCAN_MAX,
} = {}) {
  const map = new Map();          // src → trim | null (failed)
  const pending = new Map();      // src → Promise
  const retried = new Set();      // srcs already re-queued once after a timeout
  const queue = [];
  const target = typeof EventTarget === 'function' ? new EventTarget() : null;
  const keepSet = keep && typeof keep.has === 'function' ? keep : null;
  let active = 0;
  let version = 0;
  let batch = null;
  let canvas = null;
  let saveT = null;
  let dirty = false;
  let pruned = false;
  if (persist) {
    try {
      const raw = JSON.parse(window.localStorage.getItem(TRIM_KEY) || '{}');
      Object.keys(raw).forEach((src) => {
        const a = raw[src];
        const valid = Array.isArray(a) && a.length >= 6 && fracOk(a[0], a[2]) && fracOk(a[1], a[3]) && fracOk(a[4], a[5]);
        if (!valid || (keepSet && !keepSet.has(src))) { pruned = true; return; }
        map.set(src, { x0: a[0], y0: a[1], x1: a[2], y1: a[3], sx0: a[4], sx1: a[5], mode: a[6] === 'ink' ? 'ink' : 'a' });
      });
    } catch (err) { /* no storage (private mode, Node) */ }
  }
  const r4 = (v) => Math.round(v * 1e4) / 1e4;
  const save = () => {
    clearTimeout(saveT);
    saveT = null;
    if (!persist || !dirty) return;
    dirty = false;
    try {
      // Map order = oldest → newest (seeded entries first, new measurements appended): keep the newest TRIM_MAX
      const list = [];
      for (const [src, t] of map) { if (t && t.mode !== 'est') list.push([src, t]); }
      const out = {};
      list.slice(-TRIM_MAX).forEach(([src, t]) => {
        out[src] = [r4(t.x0), r4(t.y0), r4(t.x1), r4(t.y1), r4(t.sx0), r4(t.sx1), t.mode];
      });
      window.localStorage.setItem(TRIM_KEY, JSON.stringify(out));
    } catch (err) { /* quota / private mode */ }
  };
  const scheduleSave = () => {
    if (!persist) return;
    dirty = true;
    if (saveT) return;
    saveT = setTimeout(save, Math.max(0, Number(saveDelay) || 0));
  };
  const hasWin = persist && typeof window !== 'undefined' && typeof window.addEventListener === 'function';
  if (hasWin) window.addEventListener('pagehide', save);   // flush a pending write when leaving the page (removed by dispose())
  if (pruned) scheduleSave();   // rewrite the cache without the stale / invalid keys
  const emit = (src) => {
    version += 1;
    if (!batch) {
      batch = new Set();
      const flush = () => {
        const srcs = batch;
        batch = null;
        scheduleSave();
        if (target && typeof CustomEvent === 'function') target.dispatchEvent(new CustomEvent('trim', { detail: { srcs } }));
      };
      if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined' && !document.hidden) requestAnimationFrame(flush);
      else setTimeout(flush, 16);
    }
    batch.add(src);
  };
  function scan(img) {
    const W0 = img.naturalWidth;
    const H0 = img.naturalHeight;
    if (!W0 || !H0) return null;
    // measured on a canvas whose longest side is ≤ scanMax (fractions: resolution independent), whatever the download size
    const f = Math.min(1, scanMax / Math.max(W0, H0));
    const W = Math.max(1, Math.round(W0 * f));
    const H = Math.max(1, Math.round(H0 * f));
    canvas = canvas || document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const g = canvas.getContext('2d', { willReadFrequently: true });
    g.clearRect(0, 0, W, H);
    if (f < 1) g.imageSmoothingQuality = 'high';   // area-averaged downscale: thin features are not skipped
    g.drawImage(img, 0, 0, W, H);
    const d = g.getImageData(0, 0, W, H).data;        // SecurityError if tainted → caller → null
    let alpha = false;
    for (let i = 3; i < d.length; i += 4) { if (d[i] < 250) { alpha = true; break; } }
    const band = Math.max(2, Math.round(H * 0.06));
    const ay = H / 2;
    let x0 = W; let y0 = H; let x1 = -1; let y1 = -1; let sx0 = W; let sx1 = -1;
    for (let y = 0; y < H; y += 1) {
      const inBand = Math.abs(y + 0.5 - ay) <= band;
      for (let x = 0, i = y * W * 4; x < W; x += 1, i += 4) {
        const vis = alpha ? d[i + 3] > 16 : Math.min(d[i], d[i + 1], d[i + 2]) < 231;
        if (!vis) continue;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        if (inBand) { if (x < sx0) sx0 = x; if (x > sx1) sx1 = x; }
      }
    }
    canvas.width = 1; canvas.height = 1;   // free the backing store
    if (x1 < 0) return null;
    if (sx1 < 0) { sx0 = x0; sx1 = x1; }
    return { x0: x0 / W, y0: y0 / H, x1: (x1 + 1) / W, y1: (y1 + 1) / H, sx0: sx0 / W, sx1: (sx1 + 1) / W, mode: alpha ? 'a' : 'ink' };
  }
  function measure(job) {
    return new Promise((resolve) => {
      const img = new Image();
      let timer = null;
      const done = (t) => { clearTimeout(timer); img.onload = null; img.onerror = null; resolve(t); };
      timer = setTimeout(() => done(TIMED_OUT), timeout);   // the request keeps going: the retry can hit the HTTP cache
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => { try { done(scan(img)); } catch (err) { done(null); } };
      img.onerror = () => done(null);
      img.src = measureUrl(job.src, job.natW, job.side);   // same URL as the stage <img> / tiles / SSR base → one fetch
    });
  }
  function pump() {
    while (active < concurrency && queue.length) {
      const job = queue.shift();
      active += 1;
      measure(job).then((r) => {
        const timedOut = r === TIMED_OUT;
        const t = timedOut ? null : r;
        // a timeout (slow network) shows the estimate now (null = failed → EST) and is retried once later;
        // real errors (CORS, decode, empty image) stay failed for the session
        if (!(job.retry && map.get(job.src))) map.set(job.src, t);
        pending.delete(job.src);
        active -= 1;
        job.resolve(t);
        emit(job.src);
        if (timedOut && !retried.has(job.src)) {
          retried.add(job.src);
          setTimeout(() => {
            if (map.get(job.src) !== null || pending.has(job.src)) return;
            const p = new Promise((resolve) => { queue.push({ src: job.src, natW: job.natW, side: job.side, resolve, retry: true }); pump(); });
            pending.set(job.src, p);
          }, retryDelay);
        }
        pump();
      });
    }
  }
  // `side`: per-call download width cap (bases: BASE_MAX_SIDE = their natural width), default maxSide (charms, 1200);
  // the scan canvas is capped separately (scanMax)
  const measureUrl = (src, natW, side) => {
    const cap = Number(side) > 0 ? Number(side) : maxSide;
    return imgUrl(src, Math.min(Number(natW) || cap, cap));
  };
  return {
    EST: TRIM_EST,
    get version() { return version; },
    get(src) { return (src && map.get(src)) || null; },
    failed(src) { return !!src && map.has(src) && map.get(src) === null; },
    set(src, t) { map.set(src, t); emit(src); },                       // tests / fixtures
    /** the URL the measurement downloads: the stage and the tiles use the same one (one fetch per image, B2 / M3) */
    url(src, natW, side) { return measureUrl(src, natW, side); },
    /** write the pending persistence now (pagehide, tests) */
    flush() { save(); },
    /** store / theme-editor teardown: drop the pagehide listener and write the pending persistence once (NIT 3) */
    dispose() {
      if (hasWin) window.removeEventListener('pagehide', save);
      save();
    },
    /** `first`: the configuration's own images (stage, add to cart) jump the queue of the list tiles;
     *  `side`: download width cap of this image (bases: BASE_MAX_SIDE, see url()) */
    load(src, natW, first = false, side = 0) {
      if (!src) return Promise.resolve(null);
      if (map.has(src)) return Promise.resolve(map.get(src));
      if (pending.has(src)) {
        if (first) {
          const i = queue.findIndex((j) => j.src === src);
          if (i > 0) queue.unshift(queue.splice(i, 1)[0]);
        }
        return pending.get(src);
      }
      if (typeof document === 'undefined' || typeof Image === 'undefined') return Promise.resolve(null);
      const p = new Promise((resolve) => {
        const job = { src, natW, side, resolve };
        if (first) queue.unshift(job); else queue.push(job);
        pump();
      });
      pending.set(src, p);
      return p;
    },
    ensure(list, ms = 2500) {
      let timer = null;
      const all = Promise.all((list || []).slice().reverse().map((j) => this.load(j.src, j.natW, true, j.side)).reverse());
      const limit = new Promise((r) => { timer = setTimeout(r, ms); });
      return Promise.race([all, limit]).then(() => { clearTimeout(timer); });
    },
    on(fn) {
      if (!target) return () => {};
      const h = (e) => fn(e.detail.srcs);
      target.addEventListener('trim', h);
      return () => target.removeEventListener('trim', h);
    },
  };
}

const toId = (v) => (v == null || v === '' ? null : Number(v));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const uniq = (arr) => Array.from(new Set(arr));
const median = (arr) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/* ———————————————————————— data normalisation ———————————————————————— */

/** Adds Maps and derived lists to the parsed data (non-destructive: the original keys stay as in C-A §6.1). */
export function normalizeData(raw) {
  const data = raw || {};
  data.shop = data.shop || {};
  data.settings = Object.assign({
    max_fallback: 20, zoom: { min: 0.5, max: 2, step: 0.1 }, fill: { d: 0.842, m: 1 }, edit_remove: 'on_save',
    start_popup: true, show_soldout: true, facets: { color: true, price: true, avail: true },
    capture: { width: 2000, quality: 0.9, bg: '#FFFFFF' }, breakpoint: 767, design_mode: false,
  }, data.settings || {});
  data.settings.zoom = Object.assign({ min: 0.5, max: 2, step: 0.1 }, data.settings.zoom || {});
  // B2: base fill per breakpoint (desktop = setting base_fill_desktop; mobile = the whole 375 frame, Figma 603:10665) and the
  // charm scale (images share one density `pxmm`; Figma-fitted rem per mm on the stage at zoom 100 %; fallback square in mm)
  data.settings.fill = Object.assign({ d: 0.842, m: 1 }, data.settings.fill || {});
  data.settings.charm = Object.assign({ pxmm: 10, stage: { d: 0.66, m: 0.28 }, fallback_mm: 10 }, data.settings.charm || {});
  data.settings.charm.stage = Object.assign({ d: 0.66, m: 0.28 }, data.settings.charm.stage || {});
  data.groups = Array.isArray(data.groups) ? data.groups : [];
  data.charm_groups = Array.isArray(data.charm_groups) ? data.charm_groups : [];
  data.products = data.products || {};
  data.variants = data.variants || {};
  data.colors = data.colors || {};
  if (data.productsById instanceof Map) return data; // already normalised

  data.productsById = new Map(Object.values(data.products).map((p) => [Number(p.id), p]));
  data.variantsById = new Map(Object.values(data.variants).map((v) => [Number(v.id), v]));

  // Drop ids that did not make it into the maps (unpublished, missing) so every list is safe to dereference.
  for (const g of data.groups) {
    g.subgroups = (g.subgroups || []).map((sg) => ({ ...sg, products: (sg.products || []).map(Number).filter((id) => data.productsById.has(id)) }));
  }
  data.charm_groups = data.charm_groups.map((cg) => ({ ...cg, id: String(cg.id), products: (cg.products || []).map(Number).filter((id) => data.productsById.has(id)) }));
  for (const p of data.productsById.values()) {
    p.variants = (p.variants || []).map(Number).filter((id) => data.variantsById.has(id));
  }

  // Subgroup / group of every base product (first occurrence wins).
  data.subgroupOf = new Map();
  data.groupOf = new Map();
  for (const g of data.groups) {
    for (const sg of g.subgroups) {
      for (const pid of sg.products) {
        if (!data.subgroupOf.has(pid)) data.subgroupOf.set(pid, sg);
        if (!data.groupOf.has(pid)) data.groupOf.set(pid, g);
      }
    }
  }
  // Every browsable charm product, group order, deduplicated.
  data.charmProducts = uniq(data.charm_groups.flatMap((cg) => cg.products));
  // refW = drawn width (mu) of every base at 100 % = the fill. Numerically the widest base configurator image (px), fallback
  // 4500 — only a unit: no base is drawn from its own pixel width (R2) nor from its length option (wave 8), see baseGeometry.
  let refW = 0;
  for (const p of data.productsById.values()) {
    if (p.kind !== 'base') continue;
    for (const vid of p.variants) {
      const v = data.variantsById.get(vid);
      if (v && v.cfg && v.cfg.w > refW) refW = v.cfg.w;
    }
  }
  data.refW = refW || 4500;
  // Colour keys used by at least one browsable charm variant (facet order = data.colors order).
  const used = new Set();
  for (const pid of data.charmProducts) {
    for (const vid of data.productsById.get(pid).variants) (data.variantsById.get(vid).colors || []).forEach((k) => used.add(k));
  }
  data.colorKeys = Object.keys(data.colors).filter((k) => used.has(k));
  if (!data.price_max) {
    data.price_max = 0;
    for (const pid of data.charmProducts) {
      for (const vid of data.productsById.get(pid).variants) data.price_max = Math.max(data.price_max, data.variantsById.get(vid).price || 0);
    }
  }
  return data;
}

/* ———————————————————————— store ———————————————————————— */

const EMPTY_FILTERS = Object.freeze({ colors: [], price: null, avail: [] });

export class CfgStore extends EventTarget {
  constructor(data, opts = {}) {
    super();
    this.data = normalizeData(data);
    this.opts = opts;
    this.version = 0;
    this._memo = new Map();
    this._memoVersion = -1;
    this._reserved = () => 0;
    let persist = false;
    try { persist = typeof window !== 'undefined' && !!window.localStorage; } catch (err) { /* storage blocked */ }
    // persisted trims: only the configurator images of this page (stale ?v= URLs of re-uploaded images are dropped)
    const keep = new Set();
    this.data.variantsById.forEach((v) => { if (v && v.cfg && v.cfg.src) keep.add(v.cfg.src); });
    this.trims = opts.trims || createTrims({ persist, keep });
    this._ownTrims = !opts.trims;
    this.state = {
      step: 'base', groupId: null, base: { pid: null, vid: null }, charms: [], outside: [], seq: 0, selected: null,
      browse: { group: 'all', product: null }, filters: EMPTY_FILTERS, view: { zoom: 1, panX: 0, panY: 0 },
      ui: { popup: null, summaryOpen: false, busy: false, banner: null, edit: null },
    };
  }

  /* ——— infrastructure ——— */
  getState() { return this.state; }

  /** teardown (shell destroy, theme editor re-render): releases the trims this store created (pagehide listener) */
  destroy() {
    if (this._ownTrims && this.trims && typeof this.trims.dispose === 'function') this.trims.dispose();
    this._ownTrims = false;
  }

  on(fn) {
    const h = (e) => fn(e.detail);
    this.addEventListener('change', h);
    return () => this.removeEventListener('change', h);
  }

  _commit(patch, cause) {
    const prev = this.state;
    const next = { ...prev, ...patch };
    // Banner auto-clears when nothing is left outside; summary closes when no charm is left.
    if (next.ui.banner === 'overflow' && next.outside.length === 0) next.ui = { ...next.ui, banner: null };
    if (next.ui.summaryOpen && next.charms.length + next.outside.length === 0) next.ui = { ...next.ui, summaryOpen: false };
    if (next.selected && !next.charms.some((c) => c.uid === next.selected) && !next.outside.some((c) => c.uid === next.selected)) next.selected = null;
    this.state = next;
    this.version += 1;
    this.dispatchEvent(new CustomEvent('change', { detail: { cause, state: next, prev } }));
    return next;
  }

  _m(key, fn) {
    if (this._memoVersion !== this.version) { this._memo.clear(); this._memoVersion = this.version; }
    if (!this._memo.has(key)) this._memo.set(key, fn());
    return this._memo.get(key);
  }

  product(id) { return this.data.productsById.get(toId(id)) || null; }
  variant(id) { return this.data.variantsById.get(toId(id)) || null; }
  group(id) { return this.data.groups.find((g) => g.id === id) || null; }
  charmGroup(id) { return this.data.charm_groups.find((g) => g.id === String(id)) || null; }
  subgroupOf(pid) { return this.data.subgroupOf.get(toId(pid)) || null; }
  groupOf(pid) { return this.data.groupOf.get(toId(pid)) || null; }

  /** All base products of a group (every subgroup, in order, deduplicated) → product[] */
  baseProducts(groupId = this.state.groupId) {
    const g = this.group(groupId);
    if (!g) return [];
    return uniq(g.subgroups.flatMap((sg) => sg.products)).map((id) => this.product(id)).filter(Boolean);
  }

  /**
   * Default base of a group: the first AVAILABLE product (subgroup order: the first subgroup first) with its first
   * available variant; fallback the first product (decision "Added during development", 00-decisions-wave3).
   */
  defaultBase(groupId = this.state.groupId) {
    const list = this.baseProducts(groupId);
    const avail = list.find((p) => p.variants.some((id) => (this.variant(id) || {}).available));
    const p = avail || list[0] || null;
    const v = p ? this.defaultVariant(p.id) : null;
    return p && v ? { pid: p.id, vid: v.id } : null;
  }

  /** First available variant of a product, else the first one. */
  defaultVariant(pid) {
    const p = this.product(pid);
    if (!p || !p.variants.length) return null;
    const avail = p.variants.map((id) => this.variant(id)).find((v) => v && v.available);
    return avail || this.variant(p.variants[0]);
  }

  /** Option values joined with " - " ("Black - With Locker"); '' for default-only products. */
  variantLabel(vid) {
    const v = this.variant(vid);
    if (!v) return '';
    const p = this.product(v.pid);
    if (p && (!p.options || !p.options.length)) return '';
    const opts = (v.options || []).filter((o) => o && o !== 'Default Title');
    return opts.join(' - ');
  }

  /** Bar / sequence label of the current base (C-V §8.3): bold title + regular detail. */
  baseLabel(vid = this.state.base.vid) {
    const v = this.variant(vid);
    const p = v ? this.product(v.pid) : null;
    if (!p) return { title: '', detail: '', product: null, variant: null, subgroup: null };
    const sg = this.subgroupOf(p.id);
    const explicitName = p.name && p.name !== p.title;
    const multi = sg && sg.products.length > 1;
    const title = explicitName ? p.name : (sg ? sg.title : p.name);
    const parts = [];
    if (multi && !explicitName) parts.push(p.name);
    const vl = this.variantLabel(v.id);
    if (vl) parts.push(vl);
    return { title, detail: parts.join(' - '), product: p, variant: v, subgroup: sg };
  }

  setReservedQty(fn) { this._reserved = typeof fn === 'function' ? fn : () => 0; }
  reservedQty(vid) {
    try { return Math.max(0, Number(this._reserved(toId(vid))) || 0); } catch (e) { return 0; }
  }
  /** Signals that reserved quantities (cart) changed: components re-check canAdd / CTA states. */
  invalidate(reason = 'stock') { return this._commit({}, { type: reason }); }

  /* ——— lifecycle / steps ——— */
  init({ groupId, pid, vid } = {}) {
    const g = this.group(groupId) || this.data.groups[0] || null;
    let base = { pid: null, vid: null };
    const v0 = vid != null ? this.variant(vid) : null;
    if (v0) base = { pid: v0.pid, vid: v0.id };
    else if (pid != null && this.product(pid)) {
      const dv = this.defaultVariant(pid);
      if (dv) base = { pid: dv.pid, vid: dv.id };
    } else if (g) {
      base = this.defaultBase(g.id) || base;
    }
    return this._commit({ step: 'base', groupId: g ? g.id : null, base, selected: null }, { type: 'init' });
  }

  setStep(step) {
    if (step !== 'base' && step !== 'charms') return;
    if (step === this.state.step) return;
    this._commit({ step, selected: null, ui: { ...this.state.ui, summaryOpen: false } }, { type: 'step', step });
  }

  /** Base CTA / add-to-cart availability of the current base: { ok, reason: null|'none'|'soldout'|'stock' } */
  baseOk() {
    const v = this.variant(this.state.base.vid);
    if (!v) return { ok: false, reason: 'none' };
    if (!v.available) return { ok: false, reason: 'soldout' };
    if (v.stock != null && v.stock - this.reservedQty(v.id) < 1) return { ok: false, reason: 'stock' };
    return { ok: true, reason: null };
  }

  confirmBase() {
    if (!this.baseOk().ok) return false;
    this.setStep('charms');
    return true;
  }

  /* ——— base ——— */
  _applyBase(pid, vid, cause, extra = {}) {
    const s = this.state;
    const nextBase = { pid, vid };
    let charms = s.charms;
    let outside = s.outside;
    let moved = 0;
    const M = this._maxFor(vid);
    if (charms.length > M) {
      const keep = Number.isFinite(M) ? Math.max(0, M) : charms.length;
      const out = charms.slice(keep);
      moved = out.length;
      charms = charms.slice(0, keep);
      outside = [...outside, ...out];
    }
    const ui = moved ? { ...s.ui, banner: 'overflow' } : s.ui;
    this._commit({ base: nextBase, charms, outside, ui, ...extra }, { ...cause, moved, productChanged: pid !== s.base.pid });
    return { moved };
  }

  selectGroup(groupId) {
    const g = this.group(groupId);
    if (!g) return { moved: 0 };
    const d = this.defaultBase(g.id);
    if (!d) {
      this._commit({ groupId: g.id }, { type: 'group', groupId: g.id, moved: 0 });
      return { moved: 0 };
    }
    return this._applyBase(d.pid, d.vid, { type: 'group', groupId: g.id }, { groupId: g.id });
  }

  selectBaseProduct(pid) {
    const p = this.product(pid);
    const v = p ? this.defaultVariant(p.id) : null;
    if (!p || !v) return { moved: 0 };
    if (p.id === this.state.base.pid) return { moved: 0 };
    return this._applyBase(p.id, v.id, { type: 'base:product', pid: p.id, vid: v.id });
  }

  selectBaseVariant(vid) {
    const v = this.variant(vid);
    if (!v) return { moved: 0 };
    if (v.id === this.state.base.vid) return { moved: 0 };
    return this._applyBase(v.pid, v.id, { type: 'base:variant', pid: v.pid, vid: v.id });
  }

  /* ——— charms ——— */
  _maxFor(vid) {
    const v = this.variant(vid);
    let m = v && v.max != null ? Number(v.max) : null;
    if (m == null || Number.isNaN(m)) m = Number(this.data.settings.max_fallback);
    if (!m || m < 0 || Number.isNaN(m)) return Infinity; // 0 / missing = unlimited
    return m;
  }

  canAdd(vid) {
    const v = this.variant(vid);
    if (!v || !v.available) return { ok: false, reason: 'soldout' };
    if (this.countOnBase() >= this.max()) return { ok: false, reason: 'max' };
    if (v.stock != null && this.qty(v.id) + 1 > v.stock - this.reservedQty(v.id)) return { ok: false, reason: 'stock' };
    return { ok: true, reason: null };
  }

  addCharm(vid) {
    const check = this.canAdd(vid);
    if (!check.ok) return { ok: false, reason: check.reason };
    const v = this.variant(vid);
    const seq = this.state.seq + 1;
    const uid = `c${seq}`;
    this._commit({ seq, charms: [...this.state.charms, { uid, vid: v.id, seq }] }, { type: 'charm:add', uid, vid: v.id });
    return { ok: true, reason: null, uid };
  }

  _remove(uids, extra = {}) {
    if (!uids.length) return;
    const set = new Set(uids);
    const s = this.state;
    this._commit({
      charms: s.charms.filter((c) => !set.has(c.uid)),
      outside: s.outside.filter((c) => !set.has(c.uid)),
      selected: set.has(s.selected) ? null : s.selected,
    }, { type: 'charm:remove', uids, ...extra });
  }

  removeInstance(uid) {
    const inst = this.instance(uid);
    if (inst) this._remove([inst.uid], { vid: inst.vid });
  }

  removeLastOfVariant(vid) {
    const id = toId(vid);
    let last = null;
    for (const c of [...this.state.charms, ...this.state.outside]) if (c.vid === id && (!last || c.seq > last.seq)) last = c;
    if (last) this._remove([last.uid], { vid: id });
  }

  removeAllOfVariant(vid) {
    const id = toId(vid);
    const uids = [...this.state.charms, ...this.state.outside].filter((c) => c.vid === id).map((c) => c.uid);
    this._remove(uids, { vid: id });
  }

  /** { uid, vid, seq, row: 'base'|'outside', index } or null */
  instance(uid) {
    let i = this.state.charms.findIndex((c) => c.uid === uid);
    if (i > -1) return { ...this.state.charms[i], row: 'base', index: i };
    i = this.state.outside.findIndex((c) => c.uid === uid);
    if (i > -1) return { ...this.state.outside[i], row: 'outside', index: i };
    return null;
  }

  /**
   * Moves an instance. `index` = position in the target row WITHOUT the dragged item (0 … row length).
   * Outside → full base = swap (C-A §7.4). Returns { ok, swapped?, changed }.
   */
  moveInstance(uid, row, index) {
    if (row !== 'base' && row !== 'outside') return { ok: false, changed: false };
    const src = this.instance(uid);
    if (!src) return { ok: false, changed: false };
    const s = this.state;
    const item = { uid: src.uid, vid: src.vid, seq: src.seq };
    let charms = s.charms.filter((c) => c.uid !== uid);
    let outside = s.outside.filter((c) => c.uid !== uid);
    let swapped;
    const idx = Math.round(Number(index) || 0);

    if (row === 'outside') {
      const i = clamp(idx, 0, outside.length);
      outside = [...outside.slice(0, i), item, ...outside.slice(i)];
    } else if (src.row === 'base') {
      const i = clamp(idx, 0, charms.length);
      charms = [...charms.slice(0, i), item, ...charms.slice(i)];
    } else {
      const M = this.max();
      if (charms.length < M) {
        const i = clamp(idx, 0, charms.length);
        charms = [...charms.slice(0, i), item, ...charms.slice(i)];
      } else {
        if (!charms.length || M <= 0) return { ok: false, changed: false };
        const j = clamp(idx, 0, charms.length - 1);
        const out = charms[j];
        swapped = out.uid;
        charms = [...charms.slice(0, j), item, ...charms.slice(j + 1)];
        const k = clamp(src.index, 0, outside.length);
        outside = [...outside.slice(0, k), out, ...outside.slice(k)];
      }
    }
    const same = (a, b) => a.length === b.length && a.every((c, i) => c.uid === b[i].uid);
    if (same(charms, s.charms) && same(outside, s.outside)) return { ok: true, changed: false };
    const to = charms.findIndex((c) => c.uid === uid) > -1
      ? { row: 'base', index: charms.findIndex((c) => c.uid === uid) }
      : { row: 'outside', index: outside.findIndex((c) => c.uid === uid) };
    this._commit({ charms, outside }, { type: 'charm:move', uid, swapped, from: { row: src.row, index: src.index }, to });
    return swapped ? { ok: true, swapped, changed: true } : { ok: true, changed: true };
  }

  select(uid) {
    const next = uid && this.instance(uid) ? uid : null;
    if (next === this.state.selected) return;
    this._commit({ selected: next }, { type: 'select', uid: next });
  }

  /* ——— browse / filters / view / ui ——— */
  setBrowse({ group, product } = {}) {
    const s = this.state.browse;
    let g = group === undefined ? s.group : (group === 'all' || group == null ? 'all' : String(group));
    if (g !== 'all' && !this.charmGroup(g)) g = 'all';
    let p = product === undefined ? (g === s.group ? s.product : null) : toId(product);
    if (p != null) {
      const inScope = g === 'all' ? this.data.charmProducts.includes(p) : this.charmGroup(g).products.includes(p);
      if (!inScope) p = null;
    }
    if (g === s.group && p === s.product) return;
    this._commit({ browse: { group: g, product: p } }, { type: 'browse', group: g, product: p });
  }

  _normFilters(f = {}) {
    const colors = uniq((f.colors || []).map(String)).filter((k) => k in this.data.colors);
    const avail = uniq((f.avail || []).map(String)).filter((k) => k === '1' || k === '0');
    let price = null;
    if (Array.isArray(f.price) && f.price.length === 2) {
      const max = this.data.price_max || 0;
      let lo = clamp(Math.round(Number(f.price[0]) || 0), 0, max);
      let hi = clamp(Math.round(Number(f.price[1]) || 0), 0, max);
      if (lo > hi) [lo, hi] = [hi, lo];
      if (lo > 0 || hi < max) price = [lo, hi];
    }
    return { colors, price, avail };
  }

  setFilters(filters) {
    this._commit({ filters: this._normFilters(filters) }, { type: 'filters' });
  }

  clearFilters() {
    this._commit({ filters: EMPTY_FILTERS }, { type: 'filters', cleared: true });
  }

  _zoomClamp(z) {
    const { min, max, step } = this.data.settings.zoom;
    const st = step || 0.1;
    const q = Math.round(Number(z) / st) * st;
    return Math.round(clamp(q, min, max) * 1000) / 1000;
  }

  setZoom(z) {
    const v = this.state.view;
    const nz = this._zoomClamp(z);
    if (nz === v.zoom) return;
    const r = nz / v.zoom;
    this._commit({ view: { zoom: nz, panX: v.panX * r, panY: v.panY * r } }, { type: 'view', what: 'zoom', from: v.zoom });
  }

  zoomBy(dir) {
    this.setZoom(this.state.view.zoom + (dir < 0 ? -1 : 1) * (this.data.settings.zoom.step || 0.1));
  }

  setPan(x, y) {
    const v = this.state.view;
    const nx = Number(x) || 0;
    const ny = Number(y) || 0;
    if (nx === v.panX && ny === v.panY) return;
    this._commit({ view: { ...v, panX: nx, panY: ny } }, { type: 'view', what: 'pan' });
  }

  resetView() {
    const v = this.state.view;
    if (v.zoom === 1 && v.panX === 0 && v.panY === 0) return;
    this._commit({ view: { zoom: 1, panX: 0, panY: 0 } }, { type: 'view', what: 'reset', from: v.zoom });
  }

  canZoom(dir) {
    const { min, max } = this.data.settings.zoom;
    const z = this.state.view.zoom;
    return dir < 0 ? z > min + 1e-9 : z < max - 1e-9;
  }

  setUi(patch = {}) {
    const ui = this.state.ui;
    const keys = Object.keys(patch).filter((k) => ui[k] !== patch[k]);
    if (!keys.length) return;
    this._commit({ ui: { ...ui, ...patch } }, { type: 'ui', keys });
  }

  /* ——— edit / reset ——— */
  /** snap = { vid, charms: [vid…], outside: [vid…] } → step 'charms'. Unknown variants are dropped (counted in missing). */
  loadConfiguration(snap = {}) {
    let missing = 0;
    const s = this.state;
    const v = this.variant(snap.vid);
    let base = s.base;
    let groupId = s.groupId;
    if (v) {
      base = { pid: v.pid, vid: v.id };
      const g = this.groupOf(v.pid);
      if (g) groupId = g.id;
    } else if (snap.vid != null) missing += 1;
    let seq = s.seq;
    const mk = (list) => (list || []).reduce((acc, id) => {
      const cv = this.variant(id);
      if (!cv) { missing += 1; return acc; }
      seq += 1;
      acc.push({ uid: `c${seq}`, vid: cv.id, seq });
      return acc;
    }, []);
    let charms = mk(snap.charms);
    let outside = mk(snap.outside);
    // The overflow banner only signals a REAL overflow (charms pushed out by a lower max now), never charms that
    // were already outside in the saved configuration.
    const M = this._maxFor(base.vid);
    let banner = null;
    if (charms.length > M) {
      outside = [...outside, ...charms.slice(M)];
      charms = charms.slice(0, M);
      banner = 'overflow';
    }
    this._commit({
      step: 'charms', base, groupId, charms, outside, seq, selected: null,
      ui: { ...s.ui, popup: null, summaryOpen: false, banner },
    }, { type: 'load', missing });
    return { missing };
  }

  toSnapshot() {
    const s = this.state;
    return { vid: s.base.vid, charms: s.charms.map((c) => c.vid), outside: s.outside.map((c) => c.vid) };
  }

  resetCharms() {
    const s = this.state;
    this._commit({ charms: [], outside: [], selected: null, ui: { ...s.ui, banner: null, summaryOpen: false } }, { type: 'reset' });
  }

  /* ——— selectors ——— */
  get base() {
    const v = this.variant(this.state.base.vid);
    return { product: v ? this.product(v.pid) : this.product(this.state.base.pid), variant: v };
  }

  max() { return this._maxFor(this.state.base.vid); }
  countOnBase() { return this.state.charms.length; }
  countOutside() { return this.state.outside.length; }
  countAll() { return this.state.charms.length + this.state.outside.length; }
  qty(vid) {
    const id = toId(vid);
    let n = 0;
    for (const c of this.state.charms) if (c.vid === id) n += 1;
    for (const c of this.state.outside) if (c.vid === id) n += 1;
    return n;
  }

  /** cents: base price + every charm (both rows) */
  total() {
    return this._m('total', () => {
      const s = this.state;
      const bv = this.variant(s.base.vid);
      let t = bv ? bv.price : 0;
      for (const c of [...s.charms, ...s.outside]) t += (this.variant(c.vid) || {}).price || 0;
      return t;
    });
  }

  /** cents of the charms only (both rows) */
  charmsTotal() {
    const bv = this.variant(this.state.base.vid);
    return this.total() - (bv ? bv.price : 0);
  }

  scopeProducts() {
    return this._m('scope', () => {
      const { group, product } = this.state.browse;
      let ids;
      if (product != null) ids = [product];
      else if (group === 'all') ids = this.data.charmProducts;
      else ids = (this.charmGroup(group) || { products: [] }).products;
      return ids.map((id) => this.product(id)).filter(Boolean);
    });
  }

  /** scope variants (product order, variant order) honouring show_soldout, before facet filters */
  _scopeVariants() {
    return this._m('scopeV', () => {
      const showSold = this.data.settings.show_soldout !== false;
      const out = [];
      for (const p of this.scopeProducts()) {
        for (const vid of p.variants) {
          const v = this.variant(vid);
          if (v && (showSold || v.available)) out.push(v);
        }
      }
      return out;
    });
  }

  _match(v, f, skip) {
    if (skip !== 'colors' && f.colors.length && !(v.colors || []).some((k) => f.colors.includes(k))) return false;
    if (skip !== 'price' && f.price && (v.price < f.price[0] || v.price > f.price[1])) return false;
    if (skip !== 'avail' && f.avail.length && !f.avail.includes(v.available ? '1' : '0')) return false;
    return true;
  }

  visibleVariants() {
    return this._m('visible', () => {
      const f = this.state.filters;
      return this._scopeVariants().filter((v) => this._match(v, f));
    });
  }

  /** Facets for arbitrary (staged) filters: counts of each facet exclude that facet's own selection. */
  facetsFor(filters) {
    const f = this._normFilters(filters || {});
    const list = this._scopeVariants();
    const inScope = new Set();
    list.forEach((v) => (v.colors || []).forEach((k) => inScope.add(k)));
    const colorCounts = new Map();
    let a1 = 0;
    let a0 = 0;
    for (const v of list) {
      if (this._match(v, f, 'colors')) (v.colors || []).forEach((k) => colorCounts.set(k, (colorCounts.get(k) || 0) + 1));
      if (this._match(v, f, 'avail')) { if (v.available) a1 += 1; else a0 += 1; }
    }
    const colors = Object.keys(this.data.colors)
      .filter((k) => inScope.has(k) || f.colors.includes(k))
      .map((k) => ({ key: k, label: this.data.colors[k].label, hex: this.data.colors[k].hex, count: colorCounts.get(k) || 0, active: f.colors.includes(k) }));
    const max = this.data.price_max || 0;
    return {
      colors,
      price: { min: 0, max, lo: f.price ? f.price[0] : 0, hi: f.price ? f.price[1] : max, active: !!f.price },
      avail: [
        { key: '1', count: a1, active: f.avail.includes('1') },
        { key: '0', count: a0, active: f.avail.includes('0') },
      ],
      results: list.filter((v) => this._match(v, f)).length,
    };
  }

  facets() { return this._m('facets', () => this.facetsFor(this.state.filters)); }

  /** "Filters (X)": X = selected VALUES — colour keys sharing a label are one row (two "Chocolate" metaobjects), price = 1 */
  activeFilterCount(filters = this.state.filters) {
    const f = filters === this.state.filters ? filters : this._normFilters(filters);
    const rows = new Set(f.colors.map((k) => String((this.data.colors[k] && this.data.colors[k].label) || k).trim().toLowerCase()));
    return rows.size + (f.price ? 1 : 0) + f.avail.length;
  }

  /** distinct variants in order of first appearance in [...charms, ...outside] → [{ variant, product, qty }] */
  summaryItems() {
    return this._m('summary', () => {
      const order = [];
      const counts = new Map();
      for (const c of [...this.state.charms, ...this.state.outside]) {
        if (!counts.has(c.vid)) order.push(c.vid);
        counts.set(c.vid, (counts.get(c.vid) || 0) + 1);
      }
      return order.map((vid) => {
        const variant = this.variant(vid);
        return { variant, product: variant ? this.product(variant.pid) : null, qty: counts.get(vid) };
      });
    });
  }

  /** mu per charm-image px for a breakpoint at zoom 100 % (refW mu = fill × frame; stage passes its own; default = desktop, used by the capture) */
  charmK(bp = 'd') {
    const s = this.data.settings;
    const c = s.charm || {};
    const remPerPx = (Number(c.stage && c.stage[bp]) || (bp === 'm' ? 0.28 : 0.66)) / (Number(c.pxmm) || 10);
    const fill = bp === 'm' ? (Number(s.fill && s.fill.m) || 1) : (Number(s.fill && s.fill.d) || 0.842);
    const frame = bp === 'm' ? 37.5 : 192;
    return remPerPx / ((fill * frame) / this.data.refW);
  }

  /**
   * Base geometry (mu): full image box + visible box (trimmed band; estimate until measured). Memoised per trims version.
   * Width = refW for every base variant (the fill, whatever the image resolution — R2 — and whatever its length option — wave 8);
   * height = the image's own aspect ratio;
   * natW / natH = the file's natural px (download URLs only); side = the URL cap of base images (BASE_MAX_SIDE).
   */
  baseGeometry() {
    return this._m(`baseGeo:${this.trims.version}`, () => {
      const refW = this.data.refW;
      const bv = this.variant(this.state.base.vid);
      const w = refW;
      if (bv && bv.cfg && bv.cfg.w) {
        const natW = bv.cfg.w;
        const natH = bv.cfg.h || natW * 0.01;
        const h = (natH * w) / natW;
        const t = this.trims.get(bv.cfg.src);
        const x = -w / 2;
        const y = -h / 2;
        let vx0 = 0; let vx1 = w; let vy0; let vy1;
        if (t) { vx0 = t.x0 * w; vx1 = t.x1 * w; vy0 = t.y0 * h; vy1 = t.y1 * h; }
        else { const e = Math.min(h, 0.06 * w); vy0 = (h - e) / 2; vy1 = (h + e) / 2; }
        return { refW, base: { x, y, w, h, src: bv.cfg.src, natW, natH, side: BASE_MAX_SIDE, placeholder: false, vid: bv.id,
          trimmed: !!t, opaque: !!(t && t.mode === 'ink'), vis: { x0: x + vx0, y0: y + vy0, x1: x + vx1, y1: y + vy1 } } };
      }
      const h = 0.01 * refW;
      return { refW, base: { x: -w / 2, y: -h / 2, w, h, src: null, natW: null, natH: null, side: BASE_MAX_SIDE, placeholder: true, vid: bv ? bv.id : null,
        trimmed: true, opaque: false, vis: { x0: -w / 2, y0: -h / 2, x1: w / 2, y1: h / 2 } } };
    });
  }

  /** Resolve the trims of the base + the charms on the base (capture), at most `ms`. */
  ensureTrims(ms = 2500) {
    const list = [];
    const { base } = this.baseGeometry();
    if (base && base.src) list.push({ src: base.src, natW: base.natW, side: base.side });
    this.state.charms.forEach((c) => {
      const v = this.variant(c.vid);
      if (v && v.cfg && v.cfg.src) list.push({ src: v.cfg.src, natW: v.cfg.w });
    });
    return this.trims.ensure(list, ms);
  }

  /**
   * Layout in model units (mu: every base = refW, C-A §7.5 + B2 / R2). Origin = stage centre on the cord axis (y = 0).
   * opts: { charmK (mu per charm px, default charmK('d')), outsideScale = 1, gapX = 0.02·refW,
   *         outsideCenterY = null (mu below the axis; overrides the computed row centre) }
   * → { refW, charmK, base: { x, y, w, h, vis, src, natW, natH, placeholder, trimmed, opaque, vid },
   *     items: [{ uid, vid, row, index, x, y, w, h, vis: {x0,y0,x1,y1}, slot: {x0,x1}, src, natW, natH, fallback, trimmed, opaque }],
   *     rows: { base: { y0, y1 }, outside: { y0, y1, cy } | null }, bbox, bboxBase }
   *   x/y/w/h = full image box (drawing); vis = visible box; slot = space on the cord (pitch). Boxes are absolute mu.
   */
  layout(opts = {}) {
    const ck = Number(opts.charmK) > 0 ? Number(opts.charmK) : this.charmK('d');
    const key = `layout:${ck.toFixed(6)}:${this.trims.version}:${opts.outsideScale || 1}:${opts.gapX || ''}:${opts.outsideCenterY == null ? '' : opts.outsideCenterY}`;
    return this._m(key, () => {
      const s = this.state;
      const { refW, base } = this.baseGeometry();
      const c = this.data.settings.charm || {};
      const fb = (Number(c.fallback_mm) || 10) * (Number(c.pxmm) || 10) * ck;     // fallback square side (mu)
      const size = (vid, sc = 1) => {
        const v = this.variant(vid);
        if (v && v.cfg && v.cfg.w) {
          const W = v.cfg.w;
          const H = v.cfg.h || v.cfg.w;
          const known = this.trims.get(v.cfg.src);
          const t = known || TRIM_EST;
          const w = W * ck * sc;
          const h = H * ck * sc;
          return { w, h, src: v.cfg.src, natW: W, natH: H, fallback: false, trimmed: !!known, opaque: t.mode === 'ink',
            vis: { x0: t.x0 * w, y0: t.y0 * h, x1: t.x1 * w, y1: t.y1 * h }, slot: { x0: t.sx0 * w, x1: t.sx1 * w } };
        }
        const f = fb * sc;
        return { w: f, h: f, src: v && v.img ? v.img.src : null, natW: v && v.img ? v.img.w : null, natH: v && v.img ? v.img.h : null,
          fallback: true, trimmed: true, opaque: true, vis: { x0: 0, y0: 0, x1: f, y1: f }, slot: { x0: 0, x1: f } };
      };
      const abs = (z, x, y) => ({ x0: x + z.vis.x0, y0: y + z.vis.y0, x1: x + z.vis.x1, y1: y + z.vis.y1 });
      const item = (cc, i, row, z, x, y, slot) => ({ uid: cc.uid, vid: cc.vid, row, index: i, x, y, w: z.w, h: z.h, src: z.src,
        natW: z.natW, natH: z.natH, fallback: z.fallback, trimmed: z.trimmed, opaque: z.opaque, vis: abs(z, x, y), slot });
      const items = [];
      // base row: contiguous slots (gap 0, C-V §5.4), centred; image centre on the axis
      const baseSizes = s.charms.map((cc) => size(cc.vid));
      const T = baseSizes.reduce((a, z) => a + (z.slot.x1 - z.slot.x0), 0);
      let cur = -T / 2;
      s.charms.forEach((cc, i) => {
        const z = baseSizes[i];
        const sw = z.slot.x1 - z.slot.x0;
        const x = cur - z.slot.x0;
        const y = -z.h / 2;
        items.push(item(cc, i, 'base', z, x, y, { x0: cur, x1: cur + sw }));
        cur += sw;
      });
      const halfOf = (z) => Math.max(z.h / 2 - z.vis.y0, z.vis.y1 - z.h / 2);
      const baseHalf = Math.max(-base.vis.y0, base.vis.y1, ...baseSizes.map(halfOf), 0);
      const rows = { base: { y0: -baseHalf, y1: baseHalf }, outside: null };
      if (s.outside.length) {
        const sc = Number(opts.outsideScale) || 1;
        const outSizes = s.outside.map((cc) => size(cc.vid, sc));
        const visH = (z) => z.vis.y1 - z.vis.y0;
        const gapY = 0.6 * (median([...baseSizes, ...outSizes].map(visH)) || fb);
        const rowH = Math.max(...outSizes.map((z) => 2 * halfOf(z)));
        const cy = opts.outsideCenterY != null ? Number(opts.outsideCenterY) : baseHalf + gapY + rowH / 2;
        const gapX = opts.gapX != null ? Number(opts.gapX) : 0.02 * refW;
        const W = outSizes.reduce((a, z) => a + (z.vis.x1 - z.vis.x0), 0) + gapX * (outSizes.length - 1);
        let ox = -W / 2;
        s.outside.forEach((cc, i) => {
          const z = outSizes[i];
          const vw = z.vis.x1 - z.vis.x0;
          const x = ox - z.vis.x0;
          const y = cy - z.h / 2;
          items.push(item(cc, i, 'outside', z, x, y, { x0: ox, x1: ox + vw }));
          ox += vw + gapX;
        });
        rows.outside = { y0: cy - rowH / 2, y1: cy + rowH / 2, cy };
      }
      const box = (list) => list.reduce((b, r) => ({
        x0: Math.min(b.x0, r.x0), y0: Math.min(b.y0, r.y0), x1: Math.max(b.x1, r.x1), y1: Math.max(b.y1, r.y1),
      }), { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
      const bboxBase = box([base.vis, ...items.filter((it) => it.row === 'base').map((it) => it.vis)]);
      const bbox = box([base.vis, ...items.map((it) => it.vis)]);
      return { refW, charmK: ck, base, items, rows, bbox, bboxBase };
    });
  }
}

export function createStore(data, opts) {
  return new CfgStore(data, opts);
}

export default createStore;
